import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Camera, Plus, Check, AlertTriangle, Wrench, ShieldCheck, Save, X, Trash2, Loader2, StickyNote, Images } from 'lucide-react';
import api, { API } from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { formatCurrency } from '../../mock';
import { compressPhoto } from '../../lib/photo';
import { VideoBlock } from '../../components/VideoBlock';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ReconDialog } from './ReconDialog';

const STATES = [['good', 'Good', 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40'],
  ['attention', 'Attention', 'bg-amber-500/15 text-amber-400 border-amber-500/40'],
  ['repair', 'Repair', 'bg-red-500/15 text-red-400 border-red-500/40'],
  ['na', 'N/A', 'bg-muted/40 text-muted-foreground border-border']];
const inp = 'w-full bg-muted/40 border border-border rounded-md px-2.5 py-2 text-[13px] outline-none focus:border-primary transition-colors';
const NAV = [['exterior', 'Exterior'], ['tires', 'Tires & Wheels'], ['brakes', 'Brakes'], ['mechanical', 'Mechanical'],
  ['interior', 'Interior'], ['warning', 'Warning Lights'], ['obd', 'OBD Scan'], ['testdrive', 'Test Drive'],
  ['observacoes', 'Observações'], ['photos', 'Photos'], ['recon', 'Recon'], ['qc', 'Final QC']];

const StateBtns = ({ value, onChange, disabled, testid }) => (
  <div className="flex gap-1 shrink-0">
    {STATES.map(([k, label, cls]) => (
      <button key={k} data-testid={`${testid}-${k}`} disabled={disabled} onClick={() => onChange(value === k ? '' : k)}
        className={`px-2.5 py-2 min-w-[44px] rounded-md border text-[11px] font-semibold uppercase tracking-wide transition-colors ${value === k ? cls : 'border-border text-muted-foreground/70 hover:text-foreground'}`}>
        {label === 'N/A' ? 'N/A' : label.slice(0, 4)}
      </button>
    ))}
  </div>
);

export const InspectionDetail = ({ id, onBack, onChanged }) => {
  const [data, setData] = useState(null);
  const [items, setItems] = useState({});
  const [tires, setTires] = useState({});
  const [brakes, setBrakes] = useState({});
  const [obd, setObd] = useState({ scanned: false, no_codes: false, codes: [] });
  const [lights, setLights] = useState([]);
  const [notes, setNotes] = useState('');
  const [catalog, setCatalog] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [confirmDone, setConfirmDone] = useState(false);
  const [reconTarget, setReconTarget] = useState(null);
  const [editRecon, setEditRecon] = useState(null);
  const [section, setSection] = useState('exterior');
  const photoRef = useRef(null);
  const photoMeta = useRef({ kind: 'front', item_key: '' });

  const load = useCallback(async () => {
    const [{ data: d }, { data: c }] = await Promise.all([api.get(`/inspections/${id}`), api.get('/inspections/catalog')]);
    setData(d); setCatalog(c);
    setItems(d.inspection.items || {});
    setTires(d.inspection.tires || {});
    setBrakes(d.inspection.brakes || {});
    setObd(d.inspection.obd || { scanned: false, no_codes: false, codes: [] });
    setLights(d.inspection.warning_lights || []);
    setNotes(d.inspection.notes || '');
    setDirty(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (silent = true) => {
    setSaving(true);
    try {
      const { data: d } = await api.put(`/inspections/${id}`, { items, tires, brakes, obd, warning_lights: lights, notes });
      setData(d);
      setDirty(false);
      if (!silent) toast({ title: 'Inspeção salva' });
      onChanged && onChanged();
    } catch (e) { toast({ title: 'Erro ao salvar', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  }, [id, items, tires, brakes, obd, lights, notes, onChanged]);

  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => save(true), 1500);
    return () => clearTimeout(t);
  }, [dirty, save]);

  const setItem = (key, patch) => { setItems((p) => ({ ...p, [key]: { ...(p[key] || {}), ...patch } })); setDirty(true); };
  const setTire = (pos, patch) => { setTires((p) => ({ ...p, [pos]: { ...(p[pos] || {}), ...patch } })); setDirty(true); };
  const setBrake = (pos, patch) => { setBrakes((p) => ({ ...p, [pos]: { ...(p[pos] || {}), ...patch } })); setDirty(true); };

  const progress = useMemo(() => {
    if (!catalog) return { checked: 0, total: 1, pct: 0 };
    const checked = Object.values(items).filter((v) => v?.state).length
      + catalog.tire_positions.filter((t) => String(tires[t.key]?.tread ?? '') !== '').length
      + catalog.brake_positions.filter((b) => String(brakes[b.key]?.pad_mm ?? '') !== '').length;
    const total = catalog.total_items;
    return { checked, total, pct: Math.round((checked / total) * 100) };
  }, [items, tires, brakes, catalog]);

  const uploadPhoto = async (files) => {
    if (!files?.length) return;
    const list = Array.from(files);
    setUploading(list.length);
    let ok = 0;
    for (const raw of list) {
      try {
        const file = await compressPhoto(raw);
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', photoMeta.current.kind);
        fd.append('item_key', photoMeta.current.item_key || '');
        await api.post(`/inspections/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok += 1;
      } catch (e) { toast({ title: 'Erro na foto', description: e.response?.data?.detail || 'Falha no envio. Tente novamente.' }); }
      setUploading((n) => Math.max(0, n - 1));
    }
    setUploading(0);
    if (ok) toast({ title: `${ok} foto(s) enviada(s)` });
    await load();
  };
  const pickPhoto = (kind, item_key = '') => { photoMeta.current = { kind, item_key }; photoRef.current?.click(); };

  if (!data || !catalog) return <p className="ent-empty">Carregando inspeção...</p>;

  const i = data.inspection;
  const canEdit = data.can_edit;
  const recon = data.recon || [];
  const openRecon = recon.filter((r) => !['Completed', 'Cancelled'].includes(r.status));
  const reconCost = recon.reduce((s, r) => s + (r.totals?.total || 0), 0);
  const photosOf = (key) => (i.photos || []).filter((p) => p.item_key === key);
  const groupSections = (g) => catalog.sections.filter((s) => s.group === g);

  const complete = async (force = false) => {
    if (!force && progress.checked < progress.total) { setConfirmDone(true); return; }
    setConfirmDone(false);
    try {
      if (dirty) await save(true);
      await api.post(`/inspections/${id}/complete`);
      toast({ title: 'Inspeção finalizada' });
      await load(); onChanged && onChanged();
    } catch (e) { toast({ title: 'Erro ao finalizar', description: e.response?.data?.detail || 'Tente novamente.' }); }
  };
  const finalQc = async () => {
    try {
      await api.post(`/inspections/${id}/qc`);
      toast({ title: '✓ Final QC concluído', description: 'Veículo liberado para venda (Ready for Sale).' });
      await load(); onChanged && onChanged();
    } catch (e) { toast({ title: 'Não foi possível liberar', description: e.response?.data?.detail || '' }); }
  };
  const delRecon = async (r) => {
    if (!window.confirm(`Excluir item de Recon?\n\n${r.issue}\n\nLabor, peças e custos (${formatCurrency(r.totals?.total || 0)}) serão removidos. A inspeção NÃO será excluída.`)) return;
    try { await api.delete(`/recon/${r.id}`); toast({ title: 'Item de Recon excluído' }); await load(); onChanged && onChanged(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const ItemRow = ({ sec, item }) => {
    const key = `${sec.key}:${item.key}`;
    const v = items[key] || {};
    const flagged = ['attention', 'repair'].includes(v.state);
    const linked = recon.find((r) => r.id === v.recon_id);
    return (
      <div className="border-b border-border/60 last:border-0 px-3 py-2" data-testid={`insp-item-${key}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[13px] font-medium">{item.label}</span>
          <StateBtns testid={`state-${key}`} value={v.state || ''} disabled={!canEdit} onChange={(s) => setItem(key, { state: s })} />
        </div>
        {flagged && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <input data-testid={`note-${key}`} className={`${inp} flex-1 min-w-[180px]`} placeholder="Observação" value={v.note || ''} disabled={!canEdit} onChange={(e) => setItem(key, { note: e.target.value })} />
            <button data-testid={`photo-${key}`} disabled={!canEdit} onClick={() => pickPhoto('item', key)} className="ent-btn ent-btn-sm"><Camera className="w-3 h-3" /> Foto</button>
            {linked ? (
              <button data-testid={`recon-open-${key}`} onClick={() => setEditRecon(linked)} className={`ent-btn ent-btn-sm ${linked.status === 'Completed' ? 'text-[hsl(var(--ok))] border-[hsl(var(--ok))]/50' : 'text-[hsl(var(--warn))] border-[hsl(var(--warn))]/50'}`}>
                <Wrench className="w-3 h-3" /> Recon: {linked.status} · {formatCurrency(linked.totals?.total || 0)}
              </button>
            ) : (
              <button data-testid={`add-recon-${key}`} disabled={!canEdit} onClick={() => setReconTarget({ key, label: item.label, state: v.state, finding: v.note })} className="ent-btn ent-btn-sm text-primary border-primary/50"><Plus className="w-3 h-3" /> Add to Recon</button>
            )}
            {photosOf(key).map((p) => <img key={p.id} src={`${API}/files/${p.path}`} alt="" className="w-10 h-10 rounded border border-border object-cover" />)}
          </div>
        )}
      </div>
    );
  };

  const ExtraPhotos = () => {
    const extra = (i.photos || []).filter((p) => p.kind === 'extra');
    if (!extra.length) return <p className="text-[11px] t-label">Nenhuma foto adicional</p>;
    return (
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="insp-extra-grid">
        {extra.map((p) => (
          <div key={p.id} className="relative group">
            <a href={`${API}/files/${p.path}`} target="_blank" rel="noreferrer">
              <img src={`${API}/files/${p.path}`} alt="" className="w-full aspect-square object-cover rounded border border-border" />
            </a>
            <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[8px] px-1 truncate">{p.uploaded_by}</span>
            {canEdit && <button data-testid={`insp-extra-del-${p.id}`} onClick={async () => { await api.delete(`/inspections/${id}/photo/${p.id}`); await load(); }} className="absolute top-1 right-1 bg-black/70 rounded p-0.5 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>}
          </div>
        ))}
      </div>
    );
  };

  const SectionBlock = ({ sec }) => (    <div className="ent-panel overflow-hidden" data-testid={`insp-section-${sec.key}`}>
      <div className="ent-colhead"><span className="ent-coltitle uppercase tracking-wide">{sec.label}</span>
        <span className="ent-pill">{sec.items.filter((it) => items[`${sec.key}:${it.key}`]?.state).length}/{sec.items.length}</span>
      </div>
      <div>{sec.items.map((item) => <ItemRow key={item.key} sec={sec} item={item} />)}</div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="inspection-detail">
      <input ref={photoRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => { uploadPhoto(e.target.files); e.target.value = ''; }} />

      {/* sticky header */}
      <div className="sticky top-0 z-20 -mx-4 sm:-mx-5 px-4 sm:px-5 py-2.5 bg-card/95 backdrop-blur border-b border-border">
        <div className="flex items-center gap-3 flex-wrap">
          <button data-testid="insp-back" onClick={onBack} className="ent-btn"><ArrowLeft className="w-3.5 h-3.5" /> Fila</button>
          <div className="min-w-0">
            <p className="text-[14px] font-semibold leading-tight truncate">{i.vehicle_desc}</p>
            <p className="mono text-[11px] t-label">#{i.stock} · {(i.mileage || 0).toLocaleString('en-US')} mi · {i.vin || '—'}</p>
          </div>
          <span className={`ent-status ${i.status === 'Ready' ? 't-ok' : i.status === 'Needs Recon' ? 't-bad' : 't-warn'}`} data-testid="insp-detail-status"><span className="ent-dot" />{i.status}</span>
          <div className="flex items-center gap-2 ml-auto">
            <div className="hidden sm:flex items-center gap-2">
              <div className="h-2 w-32 bg-muted/60 rounded-sm overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${progress.pct}%` }} /></div>
              <span className="num text-[11px] font-semibold" data-testid="insp-progress">{progress.checked} / {progress.total} · {progress.pct}%</span>
            </div>
            {canEdit && <button data-testid="insp-save" onClick={() => save(false)} disabled={saving} className="ent-btn"><Save className="w-3.5 h-3.5" /> {saving ? 'Salvando...' : dirty ? 'Salvar' : 'Salvo'}</button>}
            {!!uploading && <span data-testid="insp-uploading" className="ent-tag t-warn flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Enviando foto...</span>}
            {canEdit && !i.completed_at && <button data-testid="insp-complete" onClick={() => complete(false)} className="ent-btn ent-btn-primary"><Check className="w-3.5 h-3.5" /> Finalizar inspeção</button>}
            {canEdit && i.completed_at && i.status !== 'Ready' && (
              <button data-testid="insp-qc" onClick={finalQc} disabled={!!openRecon.length} className="ent-btn ent-btn-primary disabled:opacity-50"><ShieldCheck className="w-3.5 h-3.5" /> Complete Final QC</button>
            )}
          </div>
        </div>
        <div className="flex sm:hidden items-center gap-2 mt-2">
          <div className="h-2 flex-1 bg-muted/60 rounded-sm overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${progress.pct}%` }} /></div>
          <span className="num text-[11px] font-semibold" data-testid="insp-progress-mobile">{progress.checked}/{progress.total} · {progress.pct}%</span>
        </div>
        <div className="flex gap-1.5 overflow-x-auto mt-2 pb-0.5">
          {NAV.map(([k, label]) => (
            <button key={k} data-testid={`insp-nav-${k}`} onClick={() => setSection(k)} className={`ent-btn ent-btn-sm shrink-0 ${section === k ? 'ent-btn-primary' : ''}`}>{label}</button>
          ))}
        </div>
      </div>

      {section === 'exterior' && <div className="space-y-3">{groupSections('Exterior').map((s) => <SectionBlock key={s.key} sec={s} />)}</div>}
      {section === 'mechanical' && <div className="space-y-3">{groupSections('Mechanical').map((s) => <SectionBlock key={s.key} sec={s} />)}</div>}
      {section === 'interior' && <div className="space-y-3">{groupSections('Interior').map((s) => <SectionBlock key={s.key} sec={s} />)}</div>}
      {section === 'testdrive' && <div className="space-y-3">{groupSections('Test Drive').map((s) => <SectionBlock key={s.key} sec={s} />)}</div>}

      {section === 'tires' && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3" data-testid="insp-tires">
          {catalog.tire_positions.map((pos) => {
            const t = tires[pos.key] || {};
            const linked = recon.find((r) => r.source_item_key === `tire:${pos.key}`);
            return (
              <div key={pos.key} className="ent-panel overflow-hidden">
                <div className="ent-colhead"><span className="ent-coltitle">{pos.key} · {pos.label}</span>
                  {t.tread !== undefined && t.tread !== '' && <span className={`ent-pill ${Number(t.tread) <= 4 ? 't-bad' : Number(t.tread) <= 6 ? 't-warn' : 't-ok'}`}>{t.tread}/32"</span>}
                </div>
                <div className="p-3 grid grid-cols-2 gap-2.5">
                  {catalog.tire_fields.map((fl) => (
                    <div key={fl.key} className={fl.type === 'state' ? 'col-span-2 flex items-center justify-between gap-2' : ''}>
                      {fl.type === 'state' ? (
                        <>
                          <span className="text-[12px]">{fl.label}</span>
                          <StateBtns testid={`tire-${pos.key}-${fl.key}`} value={t[fl.key] || ''} disabled={!canEdit} onChange={(s) => setTire(pos.key, { [fl.key]: s })} />
                        </>
                      ) : (
                        <>
                          <label className="ent-label block mb-1">{fl.label}</label>
                          <input data-testid={`tire-${pos.key}-${fl.key}`} type={fl.type === 'number' ? 'number' : 'text'} className={inp} disabled={!canEdit}
                            value={t[fl.key] ?? ''} onChange={(e) => setTire(pos.key, { [fl.key]: e.target.value })} />
                        </>
                      )}
                    </div>
                  ))}
                  <div className="col-span-2 flex items-center gap-2">
                    <input className={`${inp} flex-1`} placeholder="Observação" disabled={!canEdit} value={t.note || ''} onChange={(e) => setTire(pos.key, { note: e.target.value })} />
                    {linked ? (
                      <button onClick={() => setEditRecon(linked)} className="ent-btn ent-btn-sm text-[hsl(var(--warn))] border-[hsl(var(--warn))]/50"><Wrench className="w-3 h-3" /> {linked.status}</button>
                    ) : (
                      <button data-testid={`tire-recon-${pos.key}`} disabled={!canEdit} onClick={() => setReconTarget({ key: `tire:${pos.key}`, label: `Tire ${pos.key}`, state: 'repair', finding: `${t.brand || ''} ${t.size || ''} · ${t.tread || '?'}/32"` })} className="ent-btn ent-btn-sm text-primary border-primary/50"><Plus className="w-3 h-3" /> Recon</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {section === 'brakes' && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3" data-testid="insp-brakes">
          {catalog.brake_positions.map((pos) => {
            const b = brakes[pos.key] || {};
            const linked = recon.find((r) => r.source_item_key === `brake:${pos.key}`);
            const mm = Number(b.pad_mm);
            return (
              <div key={pos.key} className="ent-panel overflow-hidden">
                <div className="ent-colhead"><span className="ent-coltitle">Brakes · {pos.label}</span>
                  {b.pad_mm !== undefined && b.pad_mm !== '' && <span className={`ent-pill ${mm <= 3 ? 't-bad' : mm <= 5 ? 't-warn' : 't-ok'}`}>{b.pad_mm} mm</span>}
                </div>
                <div className="p-3 space-y-2.5">
                  <div><label className="ent-label block mb-1">Pad thickness (mm)</label>
                    <input data-testid={`brake-${pos.key}-pad`} type="number" step="0.5" className={inp} disabled={!canEdit} value={b.pad_mm ?? ''} onChange={(e) => setBrake(pos.key, { pad_mm: e.target.value })} />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12px]">Rotor condition</span>
                    <StateBtns testid={`brake-${pos.key}-rotor`} value={b.rotor || ''} disabled={!canEdit} onChange={(s) => setBrake(pos.key, { rotor: s })} />
                  </div>
                  <div className="flex items-center gap-2">
                    <input className={`${inp} flex-1`} placeholder="Observação" disabled={!canEdit} value={b.note || ''} onChange={(e) => setBrake(pos.key, { note: e.target.value })} />
                    {linked ? (
                      <button onClick={() => setEditRecon(linked)} className="ent-btn ent-btn-sm text-[hsl(var(--warn))] border-[hsl(var(--warn))]/50"><Wrench className="w-3 h-3" /> {linked.status}</button>
                    ) : (
                      <button data-testid={`brake-recon-${pos.key}`} disabled={!canEdit} onClick={() => setReconTarget({ key: `brake:${pos.key}`, label: `${pos.label} Brakes`, state: 'repair', finding: b.pad_mm ? `${b.pad_mm} mm` : '' })} className="ent-btn ent-btn-sm text-primary border-primary/50"><Plus className="w-3 h-3" /> Recon</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {section === 'warning' && (
        <div className="ent-panel p-3" data-testid="insp-warning">
          <p className="ent-label mb-2">Luzes de advertência acesas no painel (seleção múltipla)</p>
          <div className="flex gap-2 flex-wrap">
            {catalog.warning_lights.map((w) => (
              <button key={w} data-testid={`light-${w}`} disabled={!canEdit}
                onClick={() => { setLights((p) => (p.includes(w) ? p.filter((x) => x !== w) : [...p, w])); setDirty(true); }}
                className={`px-3 py-2.5 rounded-md border text-[12px] font-semibold transition-colors ${lights.includes(w) ? 'bg-red-500/15 text-red-400 border-red-500/40' : 'border-border text-muted-foreground/80 hover:text-foreground'}`}>
                <AlertTriangle className="w-3 h-3 inline mr-1" />{w}
              </button>
            ))}
          </div>
        </div>
      )}

      {section === 'obd' && (
        <div className="ent-panel p-3 space-y-3" data-testid="insp-obd">
          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-[13px] cursor-pointer"><input data-testid="obd-scanned" type="checkbox" disabled={!canEdit} checked={!!obd.scanned} onChange={(e) => { setObd({ ...obd, scanned: e.target.checked }); setDirty(true); }} className="accent-blue-500" /> Scan completed</label>
            <label className="flex items-center gap-2 text-[13px] cursor-pointer"><input data-testid="obd-nocodes" type="checkbox" disabled={!canEdit} checked={!!obd.no_codes} onChange={(e) => { setObd({ ...obd, no_codes: e.target.checked }); setDirty(true); }} className="accent-emerald-500" /> No codes found</label>
            <button data-testid="obd-add-code" disabled={!canEdit} onClick={() => { setObd({ ...obd, no_codes: false, codes: [...(obd.codes || []), { id: `c${Date.now()}`, code: '', module: '', description: '', status: 'Active', notes: '' }] }); setDirty(true); }} className="ent-btn ent-btn-sm ml-auto"><Plus className="w-3 h-3" /> DTC</button>
          </div>
          {(obd.codes || []).map((c, idx) => {
            const linked = recon.find((r) => r.source_item_key === `dtc:${c.id}`);
            return (
              <div key={c.id} className="grid grid-cols-[100px_110px_1fr_100px_28px] gap-2 items-center">
                <input data-testid={`dtc-code-${idx}`} className={inp} placeholder="P0420" value={c.code} onChange={(e) => { setObd({ ...obd, codes: obd.codes.map((x) => x.id === c.id ? { ...x, code: e.target.value.toUpperCase() } : x) }); setDirty(true); }} />
                <input className={inp} placeholder="Módulo" value={c.module} onChange={(e) => { setObd({ ...obd, codes: obd.codes.map((x) => x.id === c.id ? { ...x, module: e.target.value } : x) }); setDirty(true); }} />
                <input className={inp} placeholder="Descrição" value={c.description} onChange={(e) => { setObd({ ...obd, codes: obd.codes.map((x) => x.id === c.id ? { ...x, description: e.target.value } : x) }); setDirty(true); }} />
                {linked ? (
                  <button onClick={() => setEditRecon(linked)} className="ent-btn ent-btn-sm text-[hsl(var(--warn))] border-[hsl(var(--warn))]/50"><Wrench className="w-3 h-3" /> {linked.status}</button>
                ) : (
                  <button data-testid={`dtc-recon-${idx}`} disabled={!canEdit} onClick={() => setReconTarget({ key: `dtc:${c.id}`, label: `DTC ${c.code || ''}`, state: 'repair', finding: c.description })} className="ent-btn ent-btn-sm text-primary border-primary/50"><Plus className="w-3 h-3" /> Recon</button>
                )}
                <button disabled={!canEdit} onClick={() => { setObd({ ...obd, codes: obd.codes.filter((x) => x.id !== c.id) }); setDirty(true); }} className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
          {!(obd.codes || []).length && <p className="ent-empty">Nenhum DTC registrado</p>}
        </div>
      )}

      {section === 'observacoes' && (
        <div className="ent-panel p-3 space-y-3" data-testid="insp-notes-panel">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="ent-label flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" /> Observações gerais do técnico</p>
            {i.notes_by && <span className="text-[11px] t-label">Último registro: {i.notes_by} · {(i.notes_at || '').slice(0, 10)}</span>}
          </div>
          <textarea data-testid="insp-notes" rows={10} disabled={!canEdit} value={notes}
            onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
            placeholder="Escreva aqui tudo que você notou no veículo: ruídos, vazamentos, histórico, peças trocadas, recomendações..."
            className="w-full bg-muted/40 border border-border rounded-md px-3 py-2.5 text-[13px] leading-relaxed outline-none focus:border-primary transition-colors resize-y" />
          <div className="flex items-center justify-between gap-2 flex-wrap border-t border-border pt-3">
            <p className="text-[11px] t-label">Visível para todos os módulos (Estoque, Deals/F&I, Pós-venda) no laudo do veículo.</p>
            {canEdit && <button data-testid="insp-notes-save" onClick={() => save(false)} disabled={saving} className="ent-btn ent-btn-primary"><Save className="w-3.5 h-3.5" /> {saving ? 'Salvando...' : 'Salvar observações'}</button>}
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="ent-label flex items-center gap-1.5"><Images className="w-3.5 h-3.5" /> Fotos adicionais ({(i.photos || []).filter((p) => p.kind === 'extra').length})</p>
              {canEdit && <button data-testid="insp-notes-photo" onClick={() => pickPhoto('extra')} className="ent-btn ent-btn-sm"><Camera className="w-3 h-3" /> Adicionar foto</button>}
            </div>
            <ExtraPhotos />
          </div>
          <div className="border-t border-border pt-3">
            <VideoBlock targetType="inspection" targetId={id} videos={i.videos || []} canEdit={canEdit} onChanged={load} />
          </div>
        </div>
      )}

      {section === 'photos' && (
        <div className="ent-panel p-3 space-y-3" data-testid="insp-photos">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {catalog.required_photos.map((rp) => {
              const ph = (i.photos || []).filter((p) => p.kind === rp.key);
              return (
                <div key={rp.key} className="rounded-md border border-border p-2">
                  <p className="ent-label mb-1.5">{rp.label}</p>
                  {ph.length ? (
                    <div className="grid grid-cols-2 gap-1">
                      {ph.map((p) => (
                        <div key={p.id} className="relative">
                          <img src={`${API}/files/${p.path}`} alt="" className="w-full aspect-square object-cover rounded" />
                          {canEdit && <button onClick={async () => { await api.delete(`/inspections/${id}/photo/${p.id}`); await load(); }} className="absolute top-1 right-1 bg-black/70 rounded p-0.5 text-red-400"><X className="w-3 h-3" /></button>}
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-[11px] t-label mb-1.5">Sem foto</p>}
                  <button data-testid={`photo-req-${rp.key}`} disabled={!canEdit} onClick={() => pickPhoto(rp.key)} className="ent-btn ent-btn-sm w-full justify-center mt-1.5"><Camera className="w-3 h-3" /> Capturar</button>
                </div>
              );
            })}
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <p className="ent-label flex items-center gap-1.5"><Images className="w-3.5 h-3.5" /> Fotos adicionais (livres)</p>
              {canEdit && <button data-testid="insp-extra-photo" onClick={() => pickPhoto('extra')} className="ent-btn ent-btn-sm"><Camera className="w-3 h-3" /> Adicionar foto</button>}
            </div>
            <ExtraPhotos />
          </div>
          <div>
            <p className="ent-label mb-1.5">Fotos de itens com problema</p>
            <div className="flex gap-2 flex-wrap">
              {(i.photos || []).filter((p) => p.kind === 'item').map((p) => (
                <div key={p.id} className="relative">
                  <img src={`${API}/files/${p.path}`} alt="" className="w-20 h-20 object-cover rounded border border-border" />
                  <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[8px] px-1 truncate">{p.item_key}</span>
                </div>
              ))}
              {!(i.photos || []).some((p) => p.kind === 'item') && <p className="text-[11px] t-label">Nenhuma</p>}
            </div>
          </div>
        </div>
      )}

      {section === 'recon' && (
        <div className="space-y-3" data-testid="insp-recon-panel">
          <div className="ent-ribbon">
            <div><p className="ent-label">Itens</p><p className="ent-value mt-1">{recon.length}</p></div>
            <div><p className="ent-label">Abertos</p><p className={`ent-value mt-1 ${openRecon.length ? 't-bad' : 't-ok'}`}>{openRecon.length}</p></div>
            <div><p className="ent-label">Concluídos</p><p className="ent-value mt-1 t-ok">{recon.filter((r) => r.status === 'Completed').length}</p></div>
            <div><p className="ent-label">Labor</p><p className="ent-value mt-1">{formatCurrency(recon.reduce((s, r) => s + (r.totals?.labor || 0), 0))}</p></div>
            <div><p className="ent-label">Parts</p><p className="ent-value mt-1">{formatCurrency(recon.reduce((s, r) => s + (r.totals?.parts || 0), 0))}</p></div>
            <div><p className="ent-label">Total Recon</p><p className="ent-value mt-1" data-testid="insp-recon-total">{formatCurrency(reconCost)}</p></div>
          </div>
          {canEdit && <button data-testid="insp-recon-add" onClick={() => setReconTarget({ key: '', label: '', state: 'repair', finding: '' })} className="ent-btn ent-btn-primary"><Plus className="w-3.5 h-3.5" /> Novo item de Recon</button>}
          {recon.length === 0 ? <p className="ent-empty">Nenhum item de Recon nesta inspeção</p> : recon.map((r) => (
            <div key={r.id} className="ent-panel p-3" data-testid={`recon-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold">{r.issue}</p>
                  <p className="text-[12px] text-primary">{r.action || '—'}</p>
                  {r.source_item_label && <p className="text-[11px] t-label mt-0.5">Created from Inspection · {r.source_item_label}{r.source_finding ? ` (${r.source_finding})` : ''}</p>}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`ent-tag ${r.priority === 'Critical' ? 't-bad' : r.priority === 'High' ? 't-warn' : ''}`}>{r.priority}</span>
                  <span className={`ent-status ${r.status === 'Completed' ? 't-ok' : r.status === 'Cancelled' ? '' : 't-warn'}`}><span className="ent-dot" />{r.status}</span>
                  {canEdit && <button data-testid={`recon-edit-${r.id}`} onClick={() => setEditRecon(r)} className="ent-btn ent-btn-sm">Editar</button>}
                  {canEdit && <button data-testid={`recon-del-${r.id}`} onClick={() => delRecon(r)} className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2.5 text-[12px]">
                <div><p className="ent-label">Labor</p><p className="num font-semibold">{formatCurrency(r.totals?.labor || 0)} <span className="t-label">({(r.labor || []).reduce((s, l) => s + (Number(l.hours) || 0), 0)}h)</span></p></div>
                <div><p className="ent-label">Parts</p><p className="num font-semibold">{formatCurrency(r.totals?.parts || 0)} <span className="t-label">({(r.parts || []).length})</span></p></div>
                <div><p className="ent-label">Outros</p><p className="num font-semibold">{formatCurrency(r.totals?.other || 0)}</p></div>
                <div><p className="ent-label">Total</p><p className="num font-bold">{formatCurrency(r.totals?.total || 0)}</p></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {section === 'qc' && (
        <div className="ent-panel p-3 space-y-3" data-testid="insp-qc-panel">
          <p className="ent-label">Final QC — problemas encontrados x status do Recon</p>
          {recon.length === 0 ? <p className="ent-empty">Nenhum item de Recon — nada a validar</p> : (
            <table className="ent-table">
              <thead><tr><th>Problema (inspeção)</th><th>Ação</th><th>Custo</th><th>Status</th></tr></thead>
              <tbody>
                {recon.map((r) => (
                  <tr key={r.id} data-testid={`qc-row-${r.id}`}>
                    <td>{r.source_item_label || r.issue}</td>
                    <td className="text-foreground/75">→ {r.action || '—'}</td>
                    <td className="num">{formatCurrency(r.totals?.total || 0)}</td>
                    <td><span className={`ent-status ${r.status === 'Completed' ? 't-ok' : r.status === 'Cancelled' ? '' : 't-bad'}`}><span className="ent-dot" />{r.status === 'Completed' ? 'Completed ✓' : r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
            <div className="text-[12px] text-muted-foreground">
              {i.status === 'Ready' ? <span className="t-ok font-semibold">✓ Ready for Sale — liberado por {i.qc_by}</span>
                : openRecon.length ? <span className="t-bad">{openRecon.length} item(ns) de Recon em aberto — não é possível liberar</span>
                  : !i.completed_at ? 'Finalize a inspeção antes do Final QC' : 'Tudo pronto para liberar o veículo'}
            </div>
            {canEdit && i.status !== 'Ready' && (
              <button data-testid="insp-qc-complete" onClick={finalQc} disabled={!!openRecon.length || !i.completed_at} className="ent-btn ent-btn-primary disabled:opacity-50"><ShieldCheck className="w-3.5 h-3.5" /> Complete Final QC → Ready for Sale</button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] t-label border-t border-border pt-2.5">
            <span>Criada por {i.created_by} · {(i.created_at || '').slice(0, 10)}</span>
            <span>Iniciada por {i.started_by || '—'} {(i.started_at || '').slice(0, 10)}</span>
            <span>Concluída por {i.completed_by || '—'} {(i.completed_at || '').slice(0, 10)}</span>
            <span>Última atualização: {i.updated_by} {(i.updated_at || '').slice(0, 10)}</span>
          </div>
        </div>
      )}

      <Dialog open={confirmDone} onOpenChange={setConfirmDone}>
        <DialogContent className="max-w-md" data-testid="insp-confirm-dialog">
          <DialogHeader><DialogTitle className="text-[15px] font-semibold uppercase tracking-wide">Finalizar inspeção?</DialogTitle></DialogHeader>
          <p className="text-[13px] text-foreground/80">
            Você verificou <span className="num font-semibold">{progress.checked}</span> de <span className="num font-semibold">{progress.total}</span> itens
            ({progress.total - progress.checked} em branco). Os itens não preenchidos ficam como não verificados —
            você pode voltar e completar depois.
          </p>
          <DialogFooter className="gap-2">
            <button data-testid="insp-confirm-cancel" onClick={() => setConfirmDone(false)} className="ent-btn">Continuar inspeção</button>
            <button data-testid="insp-confirm-ok" onClick={() => complete(true)} className="ent-btn ent-btn-primary"><Check className="w-3.5 h-3.5" /> Finalizar mesmo assim</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReconDialog open={!!reconTarget} onClose={() => setReconTarget(null)} inspectionId={id} item={reconTarget} onSaved={() => { load(); onChanged && onChanged(); }} />
      <ReconDialog open={!!editRecon} onClose={() => setEditRecon(null)} inspectionId={id} existing={editRecon} onSaved={() => { load(); onChanged && onChanged(); }} />
    </div>
  );
};

export default InspectionDetail;
